// src/components/YourPath/AssistanceForm.js
import React, { useCallback } from "react";
import { Form, Image, Button } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { Editor } from "@tinymce/tinymce-react";
import { useFormik } from "formik";
import { AssistanceApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./AssistanceForm.form";
import "./AssistanceForm.scss";
import { ENV } from "../../../../Utils/constants";

const assistanceController = new AssistanceApi();

export function AssistanceForm(props) {
  const { accessToken, user } = useAuth();
  const { onClose, onReload, assistance } = props;

  // Eliminamos la lógica de pasos; este formulario crea/edita un asistente IA
  const formik = useFormik({
    initialValues: initialValues(assistance),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (assistance) {
          await assistanceController.updateAssistance(
            accessToken,
            assistance._id,
            formValue
          );
        } else {
          await assistanceController.createAssistance(
            accessToken,
            formValue,
            user.user._id
          );
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          formik.setFieldValue("miniature", URL.createObjectURL(file));
          formik.setFieldValue("file", file);
        } else {
          console.warn("Tipo de archivo no soportado:", file.type);
        }
      }
    },
    [formik]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
    multiple: false,
  });

  const getMiniature = () => {
    if (formik.values.file) {
      return formik.values.miniature;
    } else if (formik.values.miniature) {
      return `${formik.values.miniature}`;
    }
    return null;
  };

  return (
    <div className="assistance-form">
      {/* Encabezado indicando propósito del formulario */}
      <div className="assistance-form__header">
        <h3 className="assistance-form__step-title">
          {assistance ? "Editar Asistente IA" : "Datos necesarios para crear tu Asistente IA"}
        </h3>
        <p className="assistance-form__step-desc">
          Completa los campos para definir el asistente IA que guiará a tus alumnos.
        </p>
      </div>

      <Form onSubmit={formik.handleSubmit} noValidate>
        <Form.Group widths="equal">
          {/* Campo: Nombre del asistente IA */}
          <Form.Field className="assistance-form__field">
            <label>Nombre del asistente IA</label>
            <Form.Input
              name="title"
              placeholder="Ej: Asistente de Matemáticas"
              onChange={formik.handleChange}
              value={formik.values.title}
              error={
                formik.errors.title
                  ? { content: formik.errors.title, pointing: "above" }
                  : null
              }
            />
          </Form.Field>

          {/* Campo: Institución o Centro Educativo */}
          <Form.Field className="assistance-form__field">
            <label>Centro Educativo</label>
            <Form.Input
              name="path"
              placeholder="Ej: Instituto Superior XYZ"
              onChange={formik.handleChange}
              value={formik.values.path}
              error={
                formik.errors.path
                  ? { content: formik.errors.path, pointing: "above" }
                  : null
              }
            />
          </Form.Field>
        </Form.Group>

        {/* Campo: Rol del asistente IA */}
        <Form.Field className="assistance-form__editor">
          <label>Rol del asistente IA</label>
          <Editor
            apiKey={ENV.TINY_APIKEY}
            init={{
              height: 250,
              menubar: true,
              branding: false,
              plugins:
                "advlist autolink lists link image charmap preview anchor " +
                "searchreplace visualblocks code fullscreen insertdatetime media table " +
                "help wordcount",
              toolbar:
                "undo redo | formatselect | bold italic backcolor | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | removeformat | help",
              placeholder:
                "Define el rol y la personalidad del asistente (ej. “Soy un tutor de historia que guía al alumno a través de ejercicios interactivos”).",
            }}
            initialValue={formik.values.role}
            onBlur={(e) =>
              formik.setFieldValue("role", e.target.getContent())
            }
          />
          {formik.errors.role && (
            <div className="assistance-form__error">{formik.errors.role}</div>
          )}
        </Form.Field>

        {/* Campo: Ejercicios y reglas del asistente IA */}
        <Form.Field className="assistance-form__editor">
          <label>Ejercicios y Reglas</label>
          <Editor
            apiKey={ENV.TINY_APIKEY}
            init={{
              height: 250,
              menubar: true,
              branding: false,
              plugins:
                "advlist autolink lists link image charmap preview anchor " +
                "searchreplace visualblocks code fullscreen insertdatetime media table " +
                "help wordcount",
              toolbar:
                "undo redo | formatselect | bold italic backcolor | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | removeformat | help",
              placeholder:
                "Especifica los ejercicios, reglas y objetivos que el asistente debe aplicar (ej. “Darle pistas paso a paso para resolver ecuaciones algebraicas”).",
            }}
            initialValue={formik.values.ejercicios}
            onBlur={(e) =>
              formik.setFieldValue("ejercicios", e.target.getContent())
            }
          />
          {formik.errors.ejercicios && (
            <div className="assistance-form__error">
              {formik.errors.ejercicios}
            </div>
          )}
        </Form.Field>

        {/* Campo: Imagen/Avatar del asistente IA */}
        <Form.Field className="assistance-form__field">
          <label>Avatar del Asistente IA</label>
          <div className="assistance-form__miniature" {...getRootProps()}>
            <input {...getInputProps()} />
            {getMiniature() ? (
              <Image size="small" src={getMiniature()} centered />
            ) : (
              <div>
                <span>Arrastra o haz click para subir la imagen del avatar</span>
              </div>
            )}
          </div>
          {formik.errors.miniature && (
            <div className="assistance-form__error">
              {formik.errors.miniature}
            </div>
          )}
        </Form.Field>

        {/* Botón de envío */}
        <div className="assistance-form__button">
          <Button
            primary
            fluid
            loading={formik.isSubmitting}
            type="submit"
          >
            {assistance ? "Actualizar Asistente IA" : "Crear Asistente IA"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
